import { observer } from "mobx-react";
import React from "react";
import {
  Modal,
  Stack,
  TextInput,
  Paper,
  Group,
  Button,
  Text,
  Code,
  Image,
  Divider,
} from "@mantine/core";
import { nip19 } from "nostr-tools";
import { IconTrash } from "@tabler/icons-react";

import { MODAL_WIDTH_FOR_DATA_INTENSIVE_MODALS } from "../../../helpers/constants";
import { BOB_MODALS } from "../../helpers/constants";
import { Bob } from "../../stores/Bob";
import { CustomModalTitle } from "../details/customModalTitle";

declare global {
  interface Window {
    nostr?: { getPublicKey: () => Promise<string> };
  }
}

const SetUserProvidedNostrPublicKey = observer(({ bob }: { bob: Bob }) => {
  const isValidNpub = (k: string) => {
    if (!k?.trim()) return false;
    try {
      return nip19.decode(k.trim()).type === "npub";
    } catch {
      return false;
    }
  };
  const isSaveEnabled = () => isValidNpub(bob.userProposedNostrPublicKey);

  const handleClose = () => {
    bob.setUserProposedNostrPublicKey("");
    bob.setOpenModal(null);
  };
  const onSave = () => {
    bob.sendDataAndDirectiveToRails("set-user-provided-nostr-public-key", {
      nostrPublicKey: bob.userProposedNostrPublicKey.trim(),
    });
  };
  const onRemove = () =>
    bob.sendDataAndDirectiveToRails(
      "remove-user-provided-nostr-public-key",
      {}
    );

  const handleImportFromExtension = async () => {
    if (!window.nostr) {
      alert("No Nostr extension found (e.g. nos2x, Alby).");
      return;
    }
    try {
      const hex = await window.nostr.getPublicKey();
      bob.setUserProposedNostrPublicKey(nip19.npubEncode(hex));
    } catch (e: any) {
      alert(`Failed to import: ${e.message}`);
    }
  };

  return (
    <Modal
      opened={bob.openModal === BOB_MODALS.SET_USER_PROVIDED_NOSTR_PUBLIC_KEY}
      onClose={handleClose}
      size={MODAL_WIDTH_FOR_DATA_INTENSIVE_MODALS}
      title={
        <CustomModalTitle
          text="Import Nostr Public Key"
          icon={
            <Image
              src="https://daxwktpz4hkwd.cloudfront.net/logos/nostr_logo_wht_lg.png"
              alt="Nostr Logo"
              w={24}
              h={24}
              fit="contain"
              style={{ flexShrink: 0, marginTop: 2 }}
            />
          }
        />
      }
    >
      <Paper withBorder shadow="md" p={24} radius="md">
        <Stack>
          {bob.userProvidedNostrPublicKey ? (
            <>
              <Text>
                You have imported the Nostr public key{" "}
                <Code component="span" style={{ wordBreak: "break-all" }}>
                  {bob.userProvidedNostrPublicKey}
                </Code>
                . You can now use {bob.lightningAddress} as your NIP-05
                identifier.
              </Text>
            </>
          ) : (
            <>
              <Text>Your Rizful Lightning address is:</Text>
              <Code block>{bob.lightningAddress}</Code>

              <Text>You can use this address as a NIP-05 identifier.</Text>

              <Text>
                To do this, Rizful needs your Nostr public key, which starts
                with "npub..."
              </Text>

              <TextInput
                placeholder="npub1..."
                value={bob.userProposedNostrPublicKey}
                disabled={bob.areWeWaitingOnRails}
                description="Paste the npub of your existing Nostr profile."
                onChange={(e) =>
                  bob.setUserProposedNostrPublicKey(e.currentTarget.value)
                }
                error={
                  bob.userProposedNostrPublicKey && !isSaveEnabled()
                    ? "Invalid public key — must start with npub."
                    : undefined
                }
              />

              <Group justify="flex-end" mt="sm">
                <Button
                  variant="outline"
                  onClick={handleImportFromExtension}
                  disabled={
                    bob.areWeWaitingOnRails ||
                    bob.userProposedNostrPublicKey.length > 0
                  }
                >
                  Import from Nostr Extension
                </Button>
                <Button
                  onClick={onSave}
                  disabled={bob.areWeWaitingOnRails || !isSaveEnabled()}
                  loading={bob.areWeWaitingOnRails}
                >
                  Save
                </Button>
              </Group>
            </>
          )}

          <Group justify="space-between" mt="md">
            {bob.userProvidedNostrPublicKey && (
              <Button
                variant="outline"
                color="red"
                size="xs"
                leftSection={<IconTrash size={12} />}
                onClick={onRemove}
                disabled={bob.areWeWaitingOnRails}
                loading={bob.areWeWaitingOnRails}
              >
                Remove Key
              </Button>
            )}
            <Button variant="outline" onClick={handleClose}>
              Exit
            </Button>
          </Group>
        </Stack>
      </Paper>
    </Modal>
  );
});

export default SetUserProvidedNostrPublicKey;
